import React, { MutableRefObject, useCallback, useMemo, useState } from 'react'
import { lazyWithRetry } from "../utils/lazyWithRetry";
import { AeronetViewProps } from '../types'
import { GridApi, GridColDef, GridFilterModel, GridSortItem, GridValueFormatterParams, getGridSingleSelectOperators, getGridStringOperators } from '@mui/x-data-grid-pro'
import  AeronetDataGrid, { QueryResults }  from '../components/datagrid/DataGrid'
import { CacheRequestConfig } from 'axios-cache-interceptor'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { AxiosResponse } from 'axios'
import { axios } from '../api'
import queryString from 'query-string'
import { useSnackbar } from 'notistack'
import { DueDateDeletePayload, DueDateList, DueDateView } from './type'
import { useConfig } from '../hooks/useConfig'
import { whiteboardStatusLabel, whiteboardStatusOptions } from './whiteboardStatus'
import {
  ActionMenuEntry,
  ActionMenuSubheader,
} from '../components/datagrid/ActionMenu'
import { useNavigate } from 'react-router-dom'
import DueDateToolbar from './DueDateToolbar'
import { Button } from '@mui/material'
import DueDatePreview from './DueDatePreview'
import AlertDialog from '../components/AlertDialog'
import { useGlobalConfig } from '../hooks/useGlobalConfig'
import { isV2StationeryPreviewEnabled } from '../utils/stationery'
import PdfInlinePreviewDialog from '../components/pdf/PdfInlinePreviewDialog'
import LoadingSpinner from '../components/Loading'
import { buildDueDatePdfViaStationeryV2 } from './DueDatePdfV2Builder'
import { vaultFilesColumn, vaultPath } from "../components/datagrid/VaultFilesCell";
import { transformDueDateListParams } from './helpers'
import { format, isValid, parseISO } from 'date-fns'
import { daysRemainingUntilDue, isEmptyDueDate } from './dueDateDates'
import { useListingRecordTarget } from '../components/datagrid/ListingRouteBase'
import { urlFilterKey, useUrlFilterItems } from '../components/datagrid/urlFilter'
import { DUE_DATE_URL_FILTERS, DUE_WINDOW_FIELD, DUE_WINDOW_OPTIONS } from './dueWindows'
import { tenantDateFilterOperators } from '../components/datagrid/tenantDateFilter'
import UserFilterAutocomplete from '../components/datagrid/UserFilterAutocomplete'
import type { DefaultFilterConfig } from '../components/datagrid/filterPanel'
import { ASSIGNEE_FIELD, DEPARTMENT_FIELD, useWhiteboardFilterOptions } from './whiteboardFilters'
const DueDateAdvancedSearchForm = lazyWithRetry(() => import("./AdvanceSearchForm"));
interface DueDateDataGridProps extends AeronetViewProps{
  canCreateDuedate:boolean
canEditDuedate:boolean
canDeleteDuedate:boolean
/** The register this grid lists (GET /v6/duedate?view=…); omitted = every row. */
view?: DueDateView

}

/**
 * Per-view grid configuration. A template has no assignee, so Assigned To and
 * the days Remaining until a person's due date mean nothing on it; the
 * Templates mode omits those columns rather than forking the grid. Each view
 * keeps its own saved display / filter / sort under its own grid `name`, so a
 * filter saved on Due Dates is never restored over Templates.
 */
const VIEW_CONFIG: Record<DueDateView | "all", { name: string; omitFields: readonly string[] }> = {
  all: { name: 'DueDate-DataGrid', omitFields: [] },
  assigned: { name: 'DueDate-DataGrid', omitFields: [] },
  templates: { name: 'DueDate-Templates-DataGrid', omitFields: ['assigned_to', DUE_WINDOW_FIELD] },
}

/** Remaining filters by due WINDOW only (`is`), the Dashboard tiles' choices (BP-93). */
const dueWindowFilterOperators = getGridSingleSelectOperators().filter((op) => op.value === 'is')

// BP-95: each filterable column names the operators GET /v6/duedate applies (whiteboardFilters.ts).
/** Title / Reference / Comment: ilike or equals on the column. */
const TEXT_FILTER_OPERATORS = getGridStringOperators().filter((op) =>
  ['contains', 'equals', 'startsWith', 'endsWith'].includes(op.value)
)
/** A pick-list column (Section, Type, Company, Department): the id is / is not / is any of. */
const PICK_FILTER_OPERATORS = getGridSingleSelectOperators().filter((op) =>
  ['is', 'not', 'isAnyOf'].includes(op.value)
)
/** Assigned To: one user (the Default Filters field), sent as the route's `assigned_to`. */
const ASSIGNEE_FILTER_OPERATORS = getGridSingleSelectOperators().filter((op) => op.value === 'is')
/** Last Done / Due Date: the tenant date picker, a YYYY-MM-DD value (BP-83). */
const DATE_FILTER_OPERATORS = tenantDateFilterOperators()

/** A pick-list column's cell shows the served name (the options hold ids). */
const nameFormatter = (params: GridValueFormatterParams) =>
  params.value == null ? '' : String(params.value)

const formatGridDate = (
  value: unknown,
  dateFormat: string | undefined
) => {
  if (isEmptyDueDate(value) || !dateFormat) return "";
  try {
    const parsed = parseISO(String(value));
    if (!isValid(parsed)) return "";
    return format(parsed, dateFormat);
  } catch {
    return "";
  }
};


const DueDateDataGrid: React.FC<DueDateDataGridProps>  = (props) => {
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
  const {canCreateDuedate, canEditDuedate, canDeleteDuedate, view} = props
    const viewConfig = VIEW_CONFIG[view ?? 'all']
    // BP-93: `?status=1&remaining=overdue` from a Dashboard tile → the grid's initial filter.
    const urlItems = useUrlFilterItems(DUE_DATE_URL_FILTERS)
    const urlFilterItems = useMemo(
      () => urlItems.filter((item) => !viewConfig.omitFields.includes(item.field)),
      [urlItems, viewConfig]
    )
    // BP-41: the record page is a SIBLING of the mode routes; the layout declares the base.
    const recordTarget = useListingRecordTarget()
    const dueDateConfig =useConfig("whiteboard")
    const globalConfig = useGlobalConfig();
    const useStationeryV2InlinePreview = isV2StationeryPreviewEnabled(globalConfig);
    const [previewSelected, setPreviewSelected] = useState<number>();
    const [previewSelectedStatus, setPreviewSelectedStatus] =
      useState<any>();
    const [previewLoading, setPreviewLoading] = useState(false);
    const [inlinePreviewOpen, setInlinePreviewOpen] = useState(false);
    const [inlinePreviewBlob, setInlinePreviewBlob] = useState<Blob | null>(null);
    const [inlinePreviewFilename, setInlinePreviewFilename] = useState("");
    const [tabDialogOpen, setTabDialogOpen] = useState(false);
    const [deletePayload, setDeletePayload] = useState<DueDateDeletePayload | null>(
      null
    );
    
    const [selectedRows, setSelectedRows] = useState<DueDateList[]>([]);
    const currentUser = useCurrentUser();
    const statusOptions = useMemo(() => whiteboardStatusOptions(dueDateConfig), [dueDateConfig]);
    // The tenant's sections / types / departments (config → types) and the viewer's companies.
    const filterOptions = useWhiteboardFilterOptions(dueDateConfig);
    const column:GridColDef[] = useMemo(()=>{ const all: GridColDef[] = [
        {
            headerName:"ID",
            field:"id",
            flex:0.5
        },
        {
            headerName:"Title",
            field:"title",
            flex:1,
            filterOperators: TEXT_FILTER_OPERATORS,
        },
        {
            headerName:"Status",
            field:"status",
            flex:0.5,
            type: "singleSelect",
            filterable:true,
            valueOptions: statusOptions,
            valueFormatter: (params: GridValueFormatterParams) =>
              whiteboardStatusLabel(dueDateConfig, params.value),
            valueGetter: (params) => params.row?.status,
        },
        {
            headerName:"Section",
            // Filters on section_id (whiteboardFilters.ts FILTER_COLUMN).
            field:"section",
            flex:0.75,
            sortable: false,
            type: "singleSelect",
            valueOptions: filterOptions.sections,
            filterOperators: PICK_FILTER_OPERATORS,
            valueGetter:(params)=> params.row?.section?.name,
            valueFormatter: nameFormatter,
        },
        {
            headerName:"Type",
            field:"type_id",
            flex:0.75,
            sortable: false,
            type: "singleSelect",
            valueOptions: filterOptions.types,
            filterOperators: PICK_FILTER_OPERATORS,
            valueGetter:(params)=> params.row?.type_obj?.name,
            valueFormatter: nameFormatter,
        },
        {
            headerName:"Company",
            // Filters on company_id (whiteboardFilters.ts FILTER_COLUMN).
            field:"company",
            flex:0.75,
            sortable: false,
            type: "singleSelect",
            valueOptions: filterOptions.companies,
            filterOperators: PICK_FILTER_OPERATORS,
            valueGetter:(params)=> params.row?.company_obj?.name ?? "",
            valueFormatter: nameFormatter,
        },
        {
            headerName:"Department",
            // The Quick Filters row's field (quickFilterField below) and its Default select.
            field:DEPARTMENT_FIELD,
            flex:0.75,
            sortable: false,
            type: "singleSelect",
            valueOptions: filterOptions.departments,
            filterOperators: PICK_FILTER_OPERATORS,
            valueGetter:(params)=> params.row?.department_obj?.name ?? "",
            valueFormatter: nameFormatter,
        },
        {
            headerName:"Assigned To",
            // Filtered by the Default Filters user field (defaultFilterFields below).
            field:ASSIGNEE_FIELD,
            flex:0.75,
            sortable: false,
            filterOperators: ASSIGNEE_FILTER_OPERATORS,
            valueGetter:(params)=>
              params.row?.assigned_obj?.name ??
              params.row?.assigned_obj?.gecos ??
              params.row?.user_obj?.name ??
              params.row?.user_obj?.gecos ??
              ""
        },
        {
            headerName:"Reference",
            field:"reference",
            flex:0.75,
            sortable: false,
            filterOperators: TEXT_FILTER_OPERATORS,
        },
        {
            headerName:"Comment",
            field:"comment",
            flex:1,
            filterOperators: TEXT_FILTER_OPERATORS,
        },
        {
            headerName:"Last Done",
            field:"last_done",
            flex:0.75,
            filterOperators: DATE_FILTER_OPERATORS,
            valueFormatter: (params: GridValueFormatterParams) =>
              formatGridDate(params.value, globalConfig?.date_format),
        },
        {
            headerName:"Due Date",
            // `required` is the real column behind this date — on the model
            // (`WhiteBoard.required`) and on `DueDateList`. The field was
            // `due_date`, which exists in neither, so `AeronetDataGrid` sent
            // `?sort=due_date` on a header click and the whole list 422'd.
            // The valueGetter below keeps the `due_date` fallback for rows
            // that still carry it.
            field:"required",
            flex:0.75,
            filterOperators: DATE_FILTER_OPERATORS,
            valueGetter: (params) =>
              params.row?.required ?? params.row?.due_date ?? "",
            valueFormatter: (params: GridValueFormatterParams) =>
              formatGridDate(params.value, globalConfig?.date_format),
        },
        {
            headerName:"Remaining",
            field:DUE_WINDOW_FIELD,
            flex:0.5,
            // Filterable by due window only — transformDueDateListParams sends
            // the window to the route as `required` specs (dueWindows.ts).
            type: "singleSelect",
            valueOptions: DUE_WINDOW_OPTIONS,
            filterOperators: dueWindowFilterOperators,
            filterable: true,
            sortable: false,
            align: "right",
            headerAlign: "right",
            valueGetter: (params) => {
              if (params.row?.remaining != null && params.row.remaining !== "") {
                return params.row.remaining;
              }
              return daysRemainingUntilDue(
                params.row?.required ?? params.row?.due_date ?? null
              );
            },
            valueFormatter: (params: GridValueFormatterParams) =>
              params.value == null || params.value === "" ? "" : String(params.value),
        },
        vaultFilesColumn({
          flex: 0.7,
          getPaths: (row) => [vaultPath("duedate", row.id)],
        }),
    ]; return all.filter((col) => !viewConfig.omitFields.includes(col.field)) },[dueDateConfig, statusOptions, filterOptions, globalConfig?.date_format, viewConfig])

    // Quick Filters (Pattern 180): one chip per department, the tenant's whiteboard_department_parent_id
    // types (Aaron 2026-10-21: "groups of records for different rolls"). Several at once ("is any
    // of"), on both modes; shown until the viewer turns it off in FILTERS.
    const quickFilterField = useMemo(
      () => ({
        field: DEPARTMENT_FIELD,
        label: "Department",
        options: filterOptions.departments,
        multiple: true,
        quickFiltersDefault: true,
      }),
      [filterOptions.departments]
    )

    // Default Filters: Assigned To, a user lookup (where the mode has the column).
    const defaultFilterFields: DefaultFilterConfig[] = useMemo(
      () =>
        viewConfig.omitFields.includes(ASSIGNEE_FIELD)
          ? []
          : [
              {
                field: ASSIGNEE_FIELD,
                label: "Assigned To",
                renderField: (value, onChange) => (
                  <UserFilterAutocomplete value={value} onChange={onChange} label="Assigned To" />
                ),
              },
            ],
      [viewConfig]
    )

    const getData = useCallback(
        async (params: any, axiosConfig?: CacheRequestConfig) => {
                
            // return {
            //     total:0,
            //     rows:[],
            //   }
          try {
            const apiParams = transformDueDateListParams(params);
            const resp: AxiosResponse =
              await axios.get("/v6/duedate", {
                params: view ? { ...apiParams, view } : apiParams,
                paramsSerializer: (params) => queryString.stringify(params),
                ...axiosConfig,
              });
              return {
                total: resp.data.total,
                rows: resp.data.data,
              } as QueryResults<DueDateList>;
          } catch (e) {
            // @ts-ignore
            if (!axios.isCancel(e)) {
              enqueueSnackbar("Error fetching Duedate", {
                variant: "error",
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "center",
                },
              });
            }
            throw e;
          }
        },
        [enqueueSnackbar, view])

      const deleteDuedate = useCallback(() => {
        const dueDateIds = selectedRows.map((row) => row.id);
        if (!dueDateIds || dueDateIds.length === 0) {
          return;
        }
        for (const id of dueDateIds) {
          axios
            .delete(`/v6/duedate/${id}`)
            .then(() => {
              enqueueSnackbar(`Duedate deleted ${id}`, {
                variant: "success",
              });
              if (deletePayload?.apiRef) {
                deletePayload.apiRef.current.updateRows([
                  { id: id, _action: "delete" },
                ]);
              }
            })
            .catch((error) => {
              console.error(error);
              enqueueSnackbar(`Error deleting Inspection`, {
                variant: "error",
              });
            });
        }
        deletePayload?.setSelectionModel([]);
        setDeletePayload(null);
        setSelectedRows([]);
      }, [deletePayload, enqueueSnackbar, selectedRows]);


    const openDueDateControlReportPreview = useCallback(
      (duedateId: number, status: number) => {
        const rowStatus = whiteboardStatusLabel(dueDateConfig, status);

        if (useStationeryV2InlinePreview) {
          setPreviewLoading(true);
          void (async () => {
            try {
              const { blob, filename } = await buildDueDatePdfViaStationeryV2({
                duedateId,
                dueDateConfig,
                status: rowStatus,
              });
              setInlinePreviewBlob(blob);
              setInlinePreviewFilename(filename);
              setInlinePreviewOpen(true);
            } catch {
              enqueueSnackbar(`Failed to render due date ${duedateId}`, {
                variant: "error",
              });
            } finally {
              setPreviewLoading(false);
            }
          })();
          return;
        }

        setPreviewSelectedStatus(rowStatus ?? status);
        setPreviewLoading(true);
        setPreviewSelected(duedateId);
      },
      [dueDateConfig, useStationeryV2InlinePreview, enqueueSnackbar]
    );

    const actions: ActionMenuEntry[] = useMemo(() => {
      const items: ActionMenuEntry[] = [];

      if (canEditDuedate) {
        items.push(
          {
            name: "Duplicate",
            namePlural: "Duplicate",
            action: ["Duplicate", "DUPLICATE"],
            requiresSelection: true,
          },
          {
            name: "Copy To User",
            namePlural: "Copy To User",
            action: ["Copy_To_User", "COPY_TO_USER"],
            requiresSelection: true,
          },
          {
            name: "Set Status To Open",
            namePlural: "Set Status To Open",
            action: ["Set_Status_To_Open", "SET_STATUS_TO_OPEN"],
            requiresSelection: true,
          },
          {
            name: "Set Status To Close",
            namePlural: "Set Status To Close",
            action: ["Set_Status_To_Close", "SET_STATUS_TO_CLOSE"],
            requiresSelection: true,
          }
        );
      }

      if (canDeleteDuedate) {
        items.push({
          name: "Delete",
          namePlural: "Delete",
          action: ["Delete", "DELETE"],
          requiresSelection: true,
          multipleSelection: true,
          onClick: async (event: DueDateDeletePayload) => {
            const rows = [...event.selectedRows].map((row) => row);

            setTabDialogOpen(true);
            setDeletePayload(event);
            setSelectedRows(rows);
          },
        });
      }

      items.push({
        subheader: "Print",
        children: [
          {
            name: "Due Date Control Report",
            requiresSelection: true,
            preventMultiSelect: true,
            multiSelectErrorMessage:
              "Select a single row to preview the due date control report.",
            isReturnDataSelection: true,
            onClick: (payload) => {
              const first = payload?.selectedRows?.next()?.value as
                | DueDateList
                | undefined;
              if (first) {
                openDueDateControlReportPreview(first.id, first.status);
              }
            },
          },
        ],
      } satisfies ActionMenuSubheader);

      return items;
    }, [canEditDuedate, canDeleteDuedate, openDueDateControlReportPreview]);

    const onRowClick = useCallback(
      (param: any) => {
        navigate(recordTarget(param.row.id));
      },
      [navigate, recordTarget]
    );


const advancedSearchDefaultItems = useMemo(
  () => [
    {
      id: 1,
      field: "company_id",
      operator: "is",
      value: "",
    },
    {
      id: 2,
      field: "status",
      operator: "is",
      value: "",
    },
    {
      id: 3,
      field: "type_id",
      operator: "is",
      value: "",
    },
    {
      id: 4,
      field: "section_id",
      operator: "is",
      value: "",
    },
    {
      id: 6,
      field: "assigned_to",
      operator: "is",
      value: "",
    },
    {
      id: 7,
      field: "supervisor",
      operator: "is",
      value: "",
    },
    {
      id: 8,
      field: "department",
      operator: "is",
      value: "",
    },
    {
      id: 5,
      field: "date_type",
      operator: "is",
      value: "",
    },
    {
      id: 10,
      field: "date_from",
      operator: "is",
      value: "",
    },
    {
      id: 11,
      field: "date_to",
      operator: "is",
      value: "",
    },
    {
      id: 9,
      field: "endorsments",
      operator: "is",
      value: "",
    },
  ],
  []
);

const onSearch = useCallback(
  (filterModel: GridFilterModel, apiRef: MutableRefObject<GridApi>) => {
    console.log("FilterModel....", filterModel);
    apiRef.current.setFilterModel(filterModel, "upsertFilterItems");
  },
  []
);


const quickSearchItems = useMemo(
  () => [
    {
      field: "id",
      operator: "startsWith",
      label: "ID",
    },
    {
      field: "title",
      operator: "contains",
      label: "Title",
    },
    {
      field: "comment",
      operator: "contains",
      label: "Comment",
    },
    {
      field: "reference",
      operator: "contains",
      label: "Reference",
    },
  ],
  []
);

const handleClose = useCallback(() => {
  setPreviewSelected(undefined);
  setPreviewLoading(false);
}, []);

const handleInlinePreviewClose = useCallback(() => {
  setInlinePreviewOpen(false);
  setInlinePreviewBlob(null);
  setInlinePreviewFilename("");
}, []);

const initialSort: GridSortItem[] = useMemo(
  () => [{ field: "id", sort: "desc" }],
  []
);


  return (
   <>
   <AeronetDataGrid
   key={urlFilterKey(urlFilterItems)}
   initialFilterItems={urlFilterItems}
   advancedSearchForm={DueDateAdvancedSearchForm}
    advancedSearchProps={{
      items: advancedSearchDefaultItems,
      onSearch: onSearch,
    }}
    components={{
      toolbar:DueDateToolbar
    }}
    initialSort={initialSort}
    disableColumnFilter = {false}
    enableFilter = {true}
    columns={column}
    quickFilterField={quickFilterField}
    defaultFilterFields={defaultFilterFields}
    data={getData}
    name={viewConfig.name}
      checkboxSelection
    enableCreate={canCreateDuedate}
    currentUserId={currentUser?.id}
    actions={actions}
    onRowClick = {onRowClick}
    quickSearchItems={quickSearchItems}
   />
   {previewSelected ? (
        <DueDatePreview
        duedateId={previewSelected}
          status={previewSelectedStatus}
          onClose={handleClose}
          onReady={() => setPreviewLoading(false)}
        />
      ) : null}
      <LoadingSpinner open={previewLoading} />
      <PdfInlinePreviewDialog
        open={inlinePreviewOpen}
        blob={inlinePreviewBlob}
        filename={inlinePreviewFilename}
        title="Due Date Preview"
        onClose={handleInlinePreviewClose}
      />
      <AlertDialog
        title="Delete this record?"
        open={tabDialogOpen}
        handleClose={() => {
          setTabDialogOpen(false);
        }}
      >
        <Button
          onClick={() => {
            setTabDialogOpen(false);
          }}
          color="primary"
        >
          No
        </Button>
        <Button
          onClick={() => {
            setTabDialogOpen(false);
            deleteDuedate();
          }}
          color="primary"
          autoFocus
        >
          Yes
        </Button>
      </AlertDialog>
   </>
  )
}

export default DueDateDataGrid
